import { Component } from '@theme/component';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const pendingVariantIds = new Set();

/**
 * Cart reward tiers component.
 * Handles click-to-add for product reward variants when thresholds are met.
 * Auto-removes reward variants when cart drops below their threshold.
 * Progress bar and tier states are updated server-side via section re-rendering.
 *
 * @extends {Component}
 */
class CartRewardTiers extends Component {
  static observedAttributes = ['data-remove-variants'];
  #removing = false;

  updatedCallback() {
    super.updatedCallback();
    for (const button of this.querySelectorAll('[data-variant-id]')) {
      if (button instanceof HTMLElement && pendingVariantIds.has(button.dataset.variantId)) {
        button.closest('.cart-reward-tiers__tier')?.toggleAttribute('data-claimed', true);
      }
    }
  }

  /**
   * Called when data-remove-variants attribute changes (after section morph).
   * @param {string} name
   * @param {string|null} oldValue
   * @param {string|null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-remove-variants' && newValue && !this.#removing) {
      this.#removeVariants(newValue.split(','));
    }
  }

  /**
   * Removes reward variants from cart and dispatches a cart update event.
   * @param {string[]} keys - Line item keys to remove
   */
  async #removeVariants(keys) {
    if (!keys.length) return;
    this.#removing = true;

    try {
      /** @type {Record<string, number>} */
      const updates = {};
      for (const key of keys) {
        updates[key] = 0;
      }

      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({ updates }),
        }),
      });

      if (!response.ok) {
        console.error('[cart-reward-tiers] Failed to remove reward', response.status);
        return;
      }

      const cart = await response.json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-reward-tiers', {
          source: 'cart-reward-tiers',
          itemCount: cart.item_count,
        }),
      );
    } catch (error) {
      console.error('[cart-reward-tiers]', error);
    } finally {
      this.#removing = false;
    }
  }

  /**
   * Section ids of every cart-items-component, so the add request returns their
   * rendered HTML in one round-trip (drawer / cart page morph instead of re-fetching).
   * @returns {string} Comma-separated section ids, or '' if none.
   */
  #cartSectionIds() {
    const ids = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        ids.push(item.dataset.sectionId);
      }
    }
    return ids.join(',');
  }

  /**
   * Adds a reward variant to cart and dispatches a CartAddEvent.
   * @param {string} variantId - The variant ID to add
   * @param {string} tierLabel - The tier label for line property tracking
   * @returns {Promise<boolean>} Whether the add succeeded
   */
  async #addToCart(variantId, tierLabel) {
    const sections = this.#cartSectionIds();
    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1, properties: { _reward_tier: tierLabel } }],
      sections: sections || undefined,
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchConfig('json', { body }),
    });

    const result = await response.json();

    // Shopify flags add failures via a `status` field, even on a 200.
    if (!response.ok || result.status) {
      console.error('[cart-reward-tiers] Failed to add reward', result.message || response.status);
      return false;
    }

    const { item_count } = await fetch(Theme.routes.cart_url + '.js').then((r) => r.json());

    document.dispatchEvent(
      new CartAddEvent(result, 'cart-reward-tiers', {
        source: 'cart-reward-tiers',
        itemCount: item_count,
        sections: result.sections,
      }),
    );

    return true;
  }

  /**
   * Adds a reward variant to cart. Called by the global click handler.
   * @param {HTMLButtonElement} button - The clicked add button
   */
  async addReward(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled || pendingVariantIds.has(variantId)) return;

    const tier = button.closest('.cart-reward-tiers__tier');
    if (tier?.hasAttribute('data-claimed')) return;

    if (tier) tier.toggleAttribute('data-claimed', true);
    pendingVariantIds.add(variantId);

    let added = false;
    try {
      added = await this.#addToCart(variantId, button.dataset.tierLabel || 'reward');
    } catch (error) {
      console.error('[cart-reward-tiers]', error);
    }
    pendingVariantIds.delete(variantId);

    // Revert the optimistic claimed state when the add failed (returns false, no throw).
    if (!added && tier) tier.toggleAttribute('data-claimed', false);
  }
}

if (!customElements.get('cart-reward-tiers')) {
  customElements.define('cart-reward-tiers', CartRewardTiers);

  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    /** @type {HTMLElement|null} */
    const modalTrigger = target.closest('[data-reward-modal-handle]');
    if (modalTrigger) {
      const handle = modalTrigger.dataset.rewardModalHandle;
      if (!handle) return;
      /** @type {CartRewardModal|null} */
      const modal = /** @type {CartRewardModal|null} */ (document.getElementById(`cart-reward-modal--${handle}`));
      modal?.open(modalTrigger);
      return;
    }

    /** @type {HTMLButtonElement|null} */
    const variantBtn = target.closest('cart-reward-tiers [data-variant-id]');
    if (variantBtn) {
      /** @type {CartRewardTiers|null} */
      const component = variantBtn.closest('cart-reward-tiers');
      if (component) component.addReward(variantBtn);
      return;
    }
  });
}

/**
 * Card reward modal
 * Extends the shared DialogComponent
 *
 * @extends {DialogComponent}
 */
class CartRewardModal extends DialogComponent {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLElement | null} */
  #trigger = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Opens the modal and remembers the trigger so focus can return on close.
   * @param {HTMLElement | null} [trigger] - The element that opened the modal
   */
  open(trigger = null) {
    this.#trigger = trigger;
    trigger?.setAttribute('aria-expanded', 'true');
    this.showDialog();
  }

  /**
   * Restores focus to the trigger and resets its expanded state on close.
   */
  #handleClose = () => {
    const trigger = this.#trigger;
    this.#trigger = null;
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.focus();
  };

  /**
   * Closes the modal after a reward gift is successfully added, marking the
   * originating tier claimed so the row reflects the add before the section
   * morphs in the server-rendered state.
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    this.#trigger?.closest('.cart-reward-tiers__tier')?.toggleAttribute('data-claimed', true);
    if (this.refs.dialog?.open) this.closeDialog();
  };
}

if (!customElements.get('cart-reward-modal')) {
  customElements.define('cart-reward-modal', CartRewardModal);
}
